import React from "react";
import { BRAND_NAME, SUPPORT_EMAIL } from "../theme/brand";
import {
    Box,
    Flex,
    VStack,
    Text,
    Button,
    Icon,
    Divider,
    Link,
    useColorModeValue,
    Image,
} from "@chakra-ui/react";
import { RepeatIcon } from "@chakra-ui/icons";
import { LOGO_SRC as Logo } from "../theme/brand";
import {  LuMail } from "react-icons/lu";
import { BsHouse } from "react-icons/bs";
import { TbAlertTriangle } from "react-icons/tb";

const DomainError = () => {
    const bg = useColorModeValue("#F9FAFB", "gray.900");
    const cardBg = useColorModeValue("white", "gray.800");
    const textColor = useColorModeValue("gray.600", "gray.400");

    return (
        <Box minH="100vh" bg={"#f8fafc"}>
            {/* ---- Top Header ---- */}
            <Flex
                as="header"
                bg="white"
                boxShadow="sm"
                align="center"
                justify="flex-start"
                px={{ base: 6, md: 12 }}
                py={4}
                position="sticky"
                top={0}
                zIndex={10}
            >
                {/* Bounded box — logo aspect ratio is env-driven, see Header.js */}
                <Image src={Logo} alt="Logo" maxW="120px" maxH="50px" objectFit="contain" />
            </Flex>

            {/* ---- Main Content ---- */}
            <Flex
                direction="column"
                align="center"
                justify="center"
                mt={{ base: 12, md: 12 }}
                px={4}
                w={"100%"}
            >
                <Box
                    bg={cardBg}
                    p={{ base: 8, md: 10 }}
                    rounded="lg"
                    shadow="md"
                    textAlign="center"
                    maxW="lg"
                    borderWidth="1px"
                    borderColor={useColorModeValue("gray.200", "gray.700")}

                >
                    <VStack spacing={6} w={"100%"}>
                        {/* Warning Icon */}
                        <Flex
                            w="70px"
                            h="70px"
                            bg="#dc26261a"
                            rounded="full"
                            align="center"
                            justify="center"
                        >
                            <Icon as={TbAlertTriangle} w={10} h={10} color="#dc2626" />
                        </Flex>

                        {/* Heading */}
                        <Text fontSize="2xl" fontWeight="semibold" color="#000">
                            Domain Not Found
                        </Text>

                        {/* Description */}
                        <Text fontSize="sm" color={textColor} px={4}>
                            The domain you’re trying to access doesn’t exist or is not configured
                            in our system.
                        </Text>

                        {/* Reasons */}
                        <Box textAlign="left">
                            <Text fontSize="sm" color={textColor}>
                                This could happen if:
                            </Text>
                            <VStack align="start" spacing={1} mt={2}>
                                <Text fontSize="sm" color={textColor}>
                                    • The domain was recently created and hasn't propagated yet
                                </Text>
                                <Text fontSize="sm" color={textColor}>
                                    • There’s a typo in the domain name
                                </Text>
                                <Text fontSize="sm" color={textColor}>
                                    • The domain configuration needs to be updated
                                </Text>
                                <Text fontSize="sm" color={textColor}>
                                    • The account associated with this domain is inactive
                                </Text>
                            </VStack>
                        </Box>

                        {/* Buttons */}
                        <Flex gap={3} pt={2} flexWrap="wrap" justify="center">
                            <Button
                                leftIcon={<RepeatIcon />}
                                variant="outline"
                                colorScheme="gray"
                                onClick={() => window.location.reload()}
                                size={"sm"}
                            >
                                Try Again
                            </Button>
                            <Button
                                leftIcon={<BsHouse />}
                                bg={"var(--brand-primary)"}
                                color={"white"}
                                onClick={() => (window.location.href = "/")}
                                size={"sm"}
                            >
                                Go to Home
                            </Button>
                            <Button
                                leftIcon={<LuMail />}
                                variant="outline"
                                onClick={() =>
                                    (window.location.href = `mailto:${SUPPORT_EMAIL}`)
                                }
                                size={"sm"}
                            >
                                Contact Support
                            </Button>
                        </Flex>

                        <Divider mt={8}/>

                        {/* Support Info */}
                        <Text fontSize="xs" color={textColor} px={3}>
                            If you believe this is an error, please contact our support team at{" "}
                            <Link color="blue.500" href={`mailto:${SUPPORT_EMAIL}`}>
                                {SUPPORT_EMAIL}
                            </Link>{" "}
                            or try refreshing the page.
                        </Text>

                        <Text fontSize="xs" color="gray.400" pt={1}>
                            Error Code: DOMAIN_NOT_FOUND
                        </Text>
                    </VStack>
                </Box>

                <Divider mt={8} />
                <Text fontSize="xs" color="gray.400" my={8}>
                    © {new Date().getFullYear()} {BRAND_NAME}. All rights reserved.
                </Text>
            </Flex>
        </Box>
    );
};

export default DomainError;
